import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from '@reach/router';
import { Button } from '@avangard/ui/core';
import { breakpoints } from '@avangard/ui/utils';

import { useNavigate, createRouteUrl } from '@lib/routing';
import { PageStyled } from '@modules/layout/styled';
import { useBaseLayout } from '@modules/layout/templates';
import { Loader } from '@modules/layout/moleculas';
import { useStudentProfile } from '@modules/students/hooks';
import { useCurrentUser } from '@modules/user/hooks';
import { StudentProfile } from '@modules/students/organisms';
import { UserGrantActionToken, UserGrantCategoryToken } from '@modules/types/graphql';

type StudentGeneralTabProps = {
    editRouteUrl: string;
};

const Root = styled(PageStyled.Root)`
    height: 100%;
    padding-bottom: 60px;
`;

const Content = styled(PageStyled.Main)`
    display: flex;
    flex-wrap: nowrap;
    max-width: calc(100% - 215px);

    ${breakpoints.down(1200)} {
        flex-wrap: wrap;
    }

    ${breakpoints.down('sm')} {
        max-width: 100%;
    }
`;

const StudentGeneralTab = (props: StudentGeneralTabProps): React.ReactElement => {
    const { editRouteUrl } = props;

    const { t } = useTranslation(['common']);

    const location = useLocation();
    const navigate = useNavigate();

    const params = useParams();
    const studentId: string | undefined = params.id;

    const { currentUser } = useCurrentUser({ fetchPolicy: 'network-only' });
    const { studentGrants } = currentUser.can({
        [UserGrantCategoryToken.student]: [UserGrantActionToken.update],
    });

    const handleClickRedirectEdit = (): void => {
        const backLinkState = { ...(location.state as Record<string, any>) };

        navigate(createRouteUrl(editRouteUrl, { id: studentId }), { state: backLinkState });
    };

    const { setPageTitle } = useBaseLayout();

    const { studentProfile, loading: studentProfileLoading } = useStudentProfile({
        fetchPolicy: 'network-only',
        skip: !studentId,
        variables: { id: studentId as string },
    });

    React.useEffect(() => {
        if (studentProfile) {
            setPageTitle(studentProfile.fullName);
        }
    }, [studentProfile]);

    if (studentProfileLoading) {
        return <Loader />;
    }

    return (
        <Root>
            <Content>
                <StudentProfile sections={studentProfile.sections} />
            </Content>

            {studentGrants.canUpdate ? (
                <PageStyled.Actions>
                    <Button onClick={handleClickRedirectEdit}>{t('common:actions.edit')}</Button>
                </PageStyled.Actions>
            ) : null}
        </Root>
    );
};

export { StudentGeneralTab };
